import React, { useState, useEffect } from "react";
import { X, Plus, Trash2, Info } from "lucide-react";

export default function ColumnModal({
  isOpen,
  onClose,
  onSubmit,
  column,
  columns,
}) {
  const [formData, setFormData] = useState({
    display_name: "",
    data_type: "text",
    is_required: false,
    width: 150,
    settings: {},
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Settings for select type
  const [selectOptions, setSelectOptions] = useState([""]);

  // Settings for formula type
  const [formulaExpression, setFormulaExpression] = useState("");

  useEffect(() => {
    if (column) {
      setFormData({
        display_name: column.display_name || "",
        data_type: column.data_type || "text",
        is_required: column.is_required || false,
        width: column.width || 150,
        settings: column.settings || {},
      });

      // Load settings
      if (column.data_type === "select" && column.settings?.options) {
        setSelectOptions(column.settings.options);
      }
      if (column.data_type === "formula" && column.settings?.expression) {
        setFormulaExpression(column.settings.expression);
      }
    }
  }, [column]);

  if (!isOpen) return null;

  const dataTypes = [
    { value: "text", label: "텍스트" },
    { value: "number", label: "숫자" },
    { value: "double", label: "소수" },
    { value: "date", label: "날짜" },
    { value: "datetime", label: "날짜/시간" },
    { value: "boolean", label: "예/아니오" },
    { value: "select", label: "선택" },
  ];

  const validate = () => {
    const newErrors = {};

    if (!formData.display_name.trim()) {
      newErrors.display_name = "컬럼 이름은 필수입니다.";
    }

    if (formData.data_type === "select") {
      const validOptions = selectOptions.filter((opt) => opt.trim());
      if (validOptions.length === 0) {
        newErrors.options = "최소 하나의 옵션이 필요합니다.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    // Build settings based on data type
    let settings = {};

    if (formData.data_type === "select") {
      settings.options = selectOptions.filter((opt) => opt.trim());
    }

    if (formData.data_type === "number" || formData.data_type === "double") {
      settings.decimal_places = formData.data_type === "double" ? 2 : 0;
    }

    setSubmitting(true);
    try {
      await onSubmit({
        ...formData,
        settings,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      display_name: "",
      data_type: "text",
      is_required: false,
      width: 150,
      settings: {},
    });
    setErrors({});
    setSelectOptions([""]);
    setFormulaExpression("");
    onClose();
  };

  const addSelectOption = () => {
    setSelectOptions([...selectOptions, ""]);
  };

  const updateSelectOption = (index, value) => {
    const newOptions = [...selectOptions];
    newOptions[index] = value;
    setSelectOptions(newOptions);
  };

  const removeSelectOption = (index) => {
    if (selectOptions.length > 1) {
      setSelectOptions(selectOptions.filter((_, i) => i !== index));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-4 rounded-t-xl">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">
              {column ? "컬럼 수정" : "컬럼 추가"}
            </h2>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]"
        >
          <div className="space-y-5">
            {/* Column Name */}
            <div>
              <label
                htmlFor="display_name"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                컬럼 이름 <span className="text-red-500">*</span>
              </label>
              <input
                id="display_name"
                type="text"
                value={formData.display_name}
                onChange={(e) =>
                  setFormData({ ...formData, display_name: e.target.value })
                }
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  errors.display_name ? "border-red-300" : "border-gray-300"
                }`}
                placeholder="예: 거래처명"
                disabled={submitting}
              />
              {errors.display_name && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.display_name}
                </p>
              )}
            </div>

            {/* Data Type */}
            <div>
              <label
                htmlFor="data_type"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                데이터 타입
              </label>
              <select
                id="data_type"
                value={formData.data_type}
                onChange={(e) =>
                  setFormData({ ...formData, data_type: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                disabled={submitting || !!column}
              >
                {dataTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Type-specific Settings */}
            {formData.data_type === "select" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  선택 옵션
                </label>
                <div className="space-y-2">
                  {selectOptions.map((option, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={option}
                        onChange={(e) =>
                          updateSelectOption(index, e.target.value)
                        }
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="옵션 입력"
                        disabled={submitting}
                      />
                      <button
                        type="button"
                        onClick={() => removeSelectOption(index)}
                        disabled={selectOptions.length === 1}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addSelectOption}
                    className="flex items-center gap-2 px-3 py-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    옵션 추가
                  </button>
                </div>
                {errors.options && (
                  <p className="mt-1 text-sm text-red-600">{errors.options}</p>
                )}
              </div>
            )}

            {/* Required Field */}
            <div className="flex items-center gap-3">
              <input
                id="is_required"
                type="checkbox"
                checked={formData.is_required}
                onChange={(e) =>
                  setFormData({ ...formData, is_required: e.target.checked })
                }
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                disabled={submitting}
              />
              <label
                htmlFor="is_required"
                className="text-sm font-medium text-gray-700"
              >
                필수 입력 필드
              </label>
            </div>

            {/* Column Width */}
            <div>
              <label
                htmlFor="width"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                컬럼 너비 (px)
              </label>
              <input
                id="width"
                type="number"
                value={formData.width}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    width: parseInt(e.target.value) || 150,
                  })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                min="80"
                max="500"
                disabled={submitting}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={submitting}
            >
              취소
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={submitting}
            >
              {submitting
                ? column
                  ? "수정 중..."
                  : "추가 중..."
                : column
                ? "수정"
                : "추가"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
